import type { ReactNode } from 'react';

import {
  Button,
  cn,
  Icon,
  type IconType,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@kognitos/lattice';

/**
 * Props for the TooltipDisabledButton component.
 */
export interface ITooltipDisabledButtonProps {
  /**
   * Tooltip text shown on hover, typically explaining why the button is
   * disabled. **Must be a translated string** — pass the result of
   * `t('your.key')`, not a raw literal.
   */
  tooltip: string;
  /** Lucide icon name to display in the button. */
  icon?: IconType;
  /** Button variant style. */
  variant?:
    | 'default'
    | 'destructive'
    | 'outline'
    | 'secondary'
    | 'ghost'
    | 'link';
  /** Button size. */
  size?: 'icon' | 'default' | 'sm' | 'lg' | 'icon-sm' | 'icon-xs' | 'icon-lg';
  /** Optional children rendered alongside the icon (e.g. text label). */
  children?: ReactNode;
  /** HTML id attribute for the button. */
  id?: string;
  /** Test identifier. */
  'data-testid'?: string;
  /** Tour target identifier for product tours. */
  'data-tour-id'?: string;
  /** Accessible label for the button. */
  'aria-label'?: string;
  /** Additional CSS classes for the button. */
  className?: string;
  /** Additional CSS classes for the wrapper span. */
  wrapperClassName?: string;
}

/** Tooltip hover delay in milliseconds. Matches TooltipActionButton. */
const TOOLTIP_DELAY_MS = 200;

/**
 * A permanently-disabled Button wrapped in a Tooltip. Use this when an action
 * is unavailable for the current user (e.g. missing permission) and you want
 * the tooltip to explain why on hover.
 *
 * @example
 * <TooltipDisabledButton
 *   tooltip={t('automation.header.editAsDraftDisabled')}
 *   icon="PencilLine"
 *   variant="outline"
 *   size="icon"
 * />
 */
export function TooltipDisabledButton({
  tooltip,
  icon,
  variant = 'outline',
  size = 'icon',
  children,
  className,
  wrapperClassName,
  ...rest
}: ITooltipDisabledButtonProps) {
  return (
    <Tooltip delayDuration={TOOLTIP_DELAY_MS} variant="primary">
      <TooltipTrigger asChild>
        {/* The wrapper span exists only so the tooltip fires on a disabled
            button (disabled elements swallow mouse events). */}
        <span className={cn('inline-flex', wrapperClassName)}>
          <Button
            variant={variant}
            size={size}
            disabled
            className={className}
            id={rest.id}
            data-testid={rest['data-testid']}
            data-tour-id={rest['data-tour-id']}
            // Only set aria-label when there's no visible text content; otherwise
            // let the children form the button's accessible name.
            aria-label={
              rest['aria-label'] ?? (children == null ? tooltip : undefined)
            }
          >
            {icon && <Icon type={icon} size="sm" />}
            {children}
          </Button>
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-sm">{tooltip}</TooltipContent>
    </Tooltip>
  );
}
